import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { ChevronDownIcon } from '../lib/icons.tsx';

export interface ChipProps extends ComponentProps<'button'> {
  /** Draws the chevron that marks the chip as opening something. */
  dropdown?: boolean;
  /** The chip is carrying a value rather than sitting at its default. */
  selected?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

/**
 * The filter pill that runs along the top of every list in the product.
 *
 * It is a button, not a select: pair it with `Menu` or `Select` through their
 * `render` prop when it opens one. The open state is a coral *border* rather
 * than a fill — the fill is reserved for the row you then pick inside.
 */
export function Chip({
  dropdown,
  selected,
  icon,
  className,
  children,
  type = 'button',
  ...props
}: ChipProps) {
  return (
    <button
      // A chip that can be chosen is a toggle, and `aria-pressed` is how a
      // toggle says so. It said it with a fill alone until 2026-09-25, which
      // is invisible to a screen reader and untestable besides.
      aria-pressed={selected === undefined ? undefined : selected}
      type={type}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-full border px-4',
        'text-bodysm font-medium whitespace-nowrap text-ink shadow-button',
        'transition-[background-color,border-color] duration-150 ease-out-quint',
        selected
          ? 'border-brand-400 bg-brand-200'
          : 'border-border-subtle bg-surface-raised hover:bg-surface-muted',
        'data-popup-open:border-brand-400',
        'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-border-subtle disabled:text-ink-faint disabled:shadow-none',
        className,
      )}
      {...props}
    >
      {icon ? <span className="flex size-4 shrink-0 items-center text-ink-dim">{icon}</span> : null}
      {children}
      {dropdown ? <ChevronDownIcon className="size-3.5 shrink-0 text-ink-muted" /> : null}
    </button>
  );
}
